/**
 * Step-by-Step Toggle Component
 *
 * Provides toggle buttons for step-by-step response modes.
 * Allows users to enable one-step-at-a-time responses with or without explanations.
 */

"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
type StepByStepToggleProps = {
  isWithExplanationEnabled: boolean;
  isNoExplanationEnabled: boolean;
  onToggleWithExplanation: () => void;
  onToggleNoExplanation: () => void;
  disabled?: boolean;
};
const StepByStepToggle: React.FC<StepByStepToggleProps> = t0 => {
  const $ = _c(26);
  const {
    isWithExplanationEnabled,
    isNoExplanationEnabled,
    onToggleWithExplanation,
    onToggleNoExplanation,
    disabled: t1
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="text-xs text-foreground/60 font-medium">Step Mode:</span>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = `group relative flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${isWithExplanationEnabled ? "bg-sky/60 text-foreground shadow-md border border-sky/50" : "bg-white/60 text-foreground hover:bg-white/80 border border-foreground/30"}`;
  let t4;
  let t5;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>;
    t5 = <span>1 Step + Explain</span>;
    $[1] = t4;
    $[2] = t5;
  } else {
    t4 = $[1];
    t5 = $[2];
  }
  let t6;
  if ($[3] !== isWithExplanationEnabled) {
    t6 = isWithExplanationEnabled && <div className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-green-400 animate-pulse" />;
    $[3] = isWithExplanationEnabled;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== disabled || $[6] !== onToggleWithExplanation || $[7] !== t3 || $[8] !== t6) {
    t7 = <button onClick={onToggleWithExplanation} disabled={disabled} className={t3} title="Toggle step-by-step mode with detailed explanations">{t4}{t5}{t6}</button>;
    $[5] = disabled;
    $[6] = onToggleWithExplanation;
    $[7] = t3;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = `group relative flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${isNoExplanationEnabled ? "bg-lavender/60 text-foreground shadow-md border border-lavender/50" : "bg-white/60 text-foreground hover:bg-white/80 border border-foreground/30"}`;
  let t10;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>;
    t10 = <span>1 Step Only</span>;
    $[10] = t10;
    $[11] = t9;
  } else {
    t10 = $[10];
    t9 = $[11];
  }
  let t11;
  if ($[12] !== isNoExplanationEnabled) {
    t11 = isNoExplanationEnabled && <div className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-green-400 animate-pulse" />;
    $[12] = isNoExplanationEnabled;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== disabled || $[15] !== onToggleNoExplanation || $[16] !== t11 || $[17] !== t8) {
    t12 = <button onClick={onToggleNoExplanation} disabled={disabled} className={t8} title="Toggle step-by-step mode with no explanations (just the step)">{t9}{t10}{t11}</button>;
    $[14] = disabled;
    $[15] = onToggleNoExplanation;
    $[16] = t11;
    $[17] = t8;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== isNoExplanationEnabled || $[20] !== isWithExplanationEnabled) {
    t13 = (isWithExplanationEnabled || isNoExplanationEnabled) && <span className="text-xs text-emerald-600 font-medium flex items-center gap-1"><svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>Active</span>;
    $[19] = isNoExplanationEnabled;
    $[20] = isWithExplanationEnabled;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t12 || $[23] !== t13 || $[24] !== t7) {
    t14 = <div className="flex items-center gap-2 mb-2">{t2}{t7}{t12}{t13}</div>;
    $[22] = t12;
    $[23] = t13;
    $[24] = t7;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  return t14;
};
export default StepByStepToggle;